// frontend/src/components/EditLoad.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useTranslation,
} from "react-i18next";

import axios from "../axiosConfig";

import CurrentLoadWorkspace from "./current-load/CurrentLoadWorkspace.jsx";

import "../styles/EditLoadWorkspace.css";

const MAX_POINTS =
  7;

const NORMAL_LIMITS = {
  length:
    13.6,

  height:
    2.7,

  width:
    2.5,
};

const SPECIAL_LIMITS = {
  length:
    20,

  height:
    5,

  width:
    4,
};

const SPECIAL_THRESHOLD = {
  length:
    13.61,

  height:
    2.71,

  width:
    2.51,
};

function normalizeText(
  value
) {
  return String(
    value ??
      ""
  ).trim();
}

function toNumberOrNull(
  value
) {
  if (
    value ===
      null ||
    value ===
      undefined ||
    String(
      value
    ).trim() ===
      ""
  ) {
    return null;
  }

  const parsed =
    Number(
      String(
        value
      ).replace(
        ",",
        "."
      )
    );

  return Number.isFinite(
    parsed
  )
    ? parsed
    : null;
}

function isValidEmail(
  value
) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    String(
      value ||
        ""
    ).trim()
  );
}

function emailsToOptions(
  raw
) {
  if (
    !raw
  ) {
    return [];
  }

  const values =
    Array.isArray(
      raw
    )
      ? raw
      : String(
          raw
        )
          .split(
            ","
          )
          .map(
            (
              value
            ) =>
              value.trim()
          );

  return values
    .map(
      (
        email
      ) =>
        String(
          email ||
            ""
        )
          .trim()
          .toLowerCase()
    )
    .filter(
      (
        email
      ) =>
        email &&
        isValidEmail(
          email
        )
    )
    .slice(
      0,
      3
    )
    .map(
      (
        email
      ) => ({
        value:
          email,

        label:
          email,
      })
    );
}

function extractClientEmails(
  client
) {
  if (
    !client ||
    typeof client !==
      "object"
  ) {
    return [];
  }

  const emails =
    new Set();

  const addEmail =
    (
      value
    ) => {
      const email =
        String(
          value ||
            ""
        )
          .trim()
          .toLowerCase();

      if (
        !email ||
        !isValidEmail(
          email
        )
      ) {
        return;
      }

      emails.add(
        email
      );
    };

  addEmail(
    client.contactEmail
  );

  addEmail(
    client.email
  );

  addEmail(
    client.mainContactEmail
  );

  addEmail(
    client.secondaryContactEmail
  );

  const possibleCollections = [
    client.contacts,
    client.contactPersons,
    client.contactPeople,
  ];

  possibleCollections.forEach(
    (
      collection
    ) => {
      let normalizedCollection =
        collection;

      if (
        typeof normalizedCollection ===
        "string"
      ) {
        try {
          normalizedCollection =
            JSON.parse(
              normalizedCollection
            );
        } catch {
          normalizedCollection =
            [];
        }
      }

      if (
        !Array.isArray(
          normalizedCollection
        )
      ) {
        return;
      }

      normalizedCollection.forEach(
        (
          contact
        ) => {
          addEmail(
            contact?.email
          );

          addEmail(
            contact?.contactEmail
          );
        }
      );
    }
  );

  return Array.from(
    emails
  ).map(
    (
      email
    ) => ({
      value:
        email,

      label:
        email,
    })
  );
}

function makeEmptyPoint() {
  return {
    id:
      null,

    sequence:
      null,

    type:
      "",

    location:
      "",

    date:
      "",

    hour:
      "",

    length:
      "",

    width:
      "",

    height:
      "",

    placeId:
      null,

    address:
      "",

    postalCode:
      "",

    city:
      "",

    country:
      "",

    gMapsLink:
      "",

    coordinates:
      "",

    latitude:
      null,

    longitude:
      null,

    contactPhone:
      "",

    workingHours:
      null,

    reference:
      "",

    loadingAccess:
      null,

    handlingEquipment:
      null,

    handlingEquipmentOther:
      "",

    showMoreFields:
      false,

    locationError:
      false,

    dateError:
      false,

    hourError:
      false,

    lengthError:
      "",

    widthError:
      "",

    heightError:
      "",

    handlingEquipmentOtherError:
      false,
  };
}

function normalizePoint(
  point = {},
  type = ""
) {
  const place =
    point.place ||
    {};

  return {
    ...makeEmptyPoint(),

    ...point,

    id:
      point.id ??
      null,

    sequence:
      point.sequence ??
      point.seq ??
      null,

    type:
      String(
        point.type ||
          type ||
          ""
      )
        .trim()
        .toUpperCase(),

    location:
      point.location ??
      place.placeName ??
      "",

    date:
      point.date
        ? String(
            point.date
          ).slice(
            0,
            10
          )
        : "",

    hour:
      point.hour
        ? String(
            point.hour
          ).slice(
            0,
            5
          )
        : "",

    length:
      point.length ===
        null ||
      point.length ===
        undefined
        ? ""
        : String(
            point.length
          ),

    width:
      point.width ===
        null ||
      point.width ===
        undefined
        ? ""
        : String(
            point.width
          ),

    height:
      point.height ===
        null ||
      point.height ===
        undefined
        ? ""
        : String(
            point.height
          ),

    placeId:
      point.placeId ??
      place.id ??
      null,

    address:
      point.address ??
      place.address ??
      "",

    postalCode:
      point.postalCode ??
      place.postalCode ??
      "",

    city:
      point.city ??
      place.city ??
      "",

    country:
      point.country ??
      place.country ??
      "",

    gMapsLink:
      point.gMapsLink ??
      place.gMapsLink ??
      "",

    coordinates:
      point.coordinates ??
      place.coordinates ??
      "",

    latitude:
      point.latitude ??
      point.lat ??
      place.latitude ??
      place.lat ??
      null,

    longitude:
      point.longitude ??
      point.lng ??
      point.lon ??
      place.longitude ??
      place.lng ??
      place.lon ??
      null,

    contactPhone:
      point.contactPhone ??
      place.contactPhone ??
      "",

    workingHours:
      point.workingHours ??
      place.workingHours ??
      null,

    reference:
      point.reference ??
      "",

    loadingAccess:
      point.loadingAccess
        ? String(
            point.loadingAccess
          )
            .trim()
            .toUpperCase()
        : null,

    handlingEquipment:
      point.handlingEquipment
        ? String(
            point.handlingEquipment
          )
            .trim()
            .toUpperCase()
        : null,

    handlingEquipmentOther:
      point.handlingEquipmentOther ??
      "",

    showMoreFields:
      false,

    locationError:
      false,

    dateError:
      false,

    hourError:
      false,

    lengthError:
      "",

    widthError:
      "",

    heightError:
      "",

    handlingEquipmentOtherError:
      false,
  };
}

function buildPointsFromPickupDeliveries(
  rows = [],
  type
) {
  return (
    Array.isArray(
      rows
    )
      ? rows
      : []
  )
    .filter(
      (
        point
      ) =>
        String(
          point?.type ||
            ""
        )
          .trim()
          .toUpperCase() ===
        type
    )
    .map(
      (
        point
      ) =>
        normalizePoint(
          point,
          type
        )
    );
}

function getPickupPoints(
  load
) {
  if (
    Array.isArray(
      load?.pickupLocations
    ) &&
    load.pickupLocations.length >
      0
  ) {
    return load.pickupLocations.map(
      (
        point
      ) =>
        normalizePoint(
          point,
          "PICKUP"
        )
    );
  }

  const pickupDeliveriesPoints =
    buildPointsFromPickupDeliveries(
      load?.pickupDeliveries,
      "PICKUP"
    );

  if (
    pickupDeliveriesPoints.length >
      0
  ) {
    return pickupDeliveriesPoints;
  }

  if (
    Array.isArray(
      load?.pickupPoints
    ) &&
    load.pickupPoints.length >
      0
  ) {
    return load.pickupPoints.map(
      (
        point
      ) =>
        normalizePoint(
          point,
          "PICKUP"
        )
    );
  }

  return [];
}

function getDeliveryPoints(
  load
) {
  if (
    Array.isArray(
      load?.deliveryLocations
    ) &&
    load.deliveryLocations.length >
      0
  ) {
    return load.deliveryLocations.map(
      (
        point
      ) =>
        normalizePoint(
          point,
          "DELIVERY"
        )
    );
  }

  const pickupDeliveriesPoints =
    buildPointsFromPickupDeliveries(
      load?.pickupDeliveries,
      "DELIVERY"
    );

  if (
    pickupDeliveriesPoints.length >
      0
  ) {
    return pickupDeliveriesPoints;
  }

  if (
    Array.isArray(
      load?.deliveryPoints
    ) &&
    load.deliveryPoints.length >
      0
  ) {
    return load.deliveryPoints.map(
      (
        point
      ) =>
        normalizePoint(
          point,
          "DELIVERY"
        )
    );
  }

  return [];
}

function sanitizePoint(
  point
) {
  const handlingEquipment =
    point.handlingEquipment
      ? String(
          point.handlingEquipment
        )
          .trim()
          .toUpperCase()
      : null;

  const latitude =
    toNumberOrNull(
      point.latitude ??
      point.lat
    );

  const longitude =
    toNumberOrNull(
      point.longitude ??
      point.lng ??
      point.lon
    );

  return {
    id:
      point.id ??
      null,

    sequence:
      point.sequence ??
      null,

    type:
      point.type
        ? String(
            point.type
          )
            .trim()
            .toUpperCase()
        : null,

    placeId:
      point.placeId ??
      null,

    location:
      normalizeText(
        point.location
      ),

    date:
      normalizeText(
        point.date
      ) ||
      null,

    hour:
      normalizeText(
        point.hour
      ) ||
      null,

    length:
      toNumberOrNull(
        point.length
      ),

    width:
      toNumberOrNull(
        point.width
      ),

    height:
      toNumberOrNull(
        point.height
      ),

    address:
      normalizeText(
        point.address
      ) ||
      null,

    postalCode:
      normalizeText(
        point.postalCode
      ) ||
      null,

    city:
      normalizeText(
        point.city
      ) ||
      null,

    country:
      normalizeText(
        point.country
      ) ||
      null,

    gMapsLink:
      normalizeText(
        point.gMapsLink
      ) ||
      null,

    coordinates:
      normalizeText(
        point.coordinates
      ) ||
      null,

    latitude,

    longitude,

    contactPhone:
      normalizeText(
        point.contactPhone
      ) ||
      null,

    reference:
      normalizeText(
        point.reference
      ) ||
      null,

    workingHours:
      point.workingHours ??
      null,

    loadingAccess:
      point.loadingAccess
        ? String(
            point.loadingAccess
          )
            .trim()
            .toUpperCase()
        : null,

    handlingEquipment,

    handlingEquipmentOther:
      handlingEquipment ===
        "OTHER"
        ? normalizeText(
            point.handlingEquipmentOther
          ) ||
          null
        : null,
  };
}

function mergeLoadData(
  current,
  incoming
) {
  const currentValue =
    current &&
    typeof current ===
      "object"
      ? current
      : {};

  const incomingValue =
    incoming &&
    typeof incoming ===
      "object"
      ? incoming
      : {};

  return {
    ...currentValue,
    ...incomingValue,
  };
}

function getTimestamp(
  point
) {
  const date =
    normalizeText(
      point?.date
    );

  const hour =
    normalizeText(
      point?.hour
    );

  if (
    !date ||
    !hour
  ) {
    return null;
  }

  const timestamp =
    new Date(
      `${date}T${hour}:00`
    ).getTime();

  return Number.isFinite(
    timestamp
  )
    ? timestamp
    : null;
}

function calculatePointTotals(
  points = []
) {
  return points.reduce(
    (
      accumulator,
      point
    ) => {
      const pointLength =
        toNumberOrNull(
          point?.length
        ) ??
        0;

      const pointWidth =
        toNumberOrNull(
          point?.width
        ) ??
        0;

      const pointHeight =
        toNumberOrNull(
          point?.height
        ) ??
        0;

      return {
        length:
          accumulator.length +
          pointLength,

        width:
          Math.max(
            accumulator.width,
            pointWidth
          ),

        height:
          Math.max(
            accumulator.height,
            pointHeight
          ),
      };
    },
    {
      length:
        0,

      width:
        0,

      height:
        0,
    }
  );
}

export default function EditLoad() {
  const {
    loadId,
  } =
    useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const {
    t,
  } =
    useTranslation(
      "edit_load"
    );

  const prefill =
    location.state
      ?.prefill ||
    location.state
      ?.load ||
    null;

  const [
    load,
    setLoad,
  ] =
    useState(
      prefill
    );

  const [
    ready,
    setReady,
  ] =
    useState(
      Boolean(
        prefill
      )
    );

  const [
    fetching,
    setFetching,
  ] =
    useState(
      false
    );

  const [
    saving,
    setSaving,
  ] =
    useState(
      false
    );

  const [
    error,
    setError,
  ] =
    useState(
      ""
    );

  const [
    successPopupOpen,
    setSuccessPopupOpen,
  ] =
    useState(
      false
    );

  const [
    savedEditSnapshot,
    setSavedEditSnapshot,
  ] =
    useState(
      null
    );

  const [
    unsavedChangesModalOpen,
    setUnsavedChangesModalOpen,
  ] =
    useState(
      false
    );

  const [
    pendingNavigation,
    setPendingNavigation,
  ] =
    useState(
      null
    );

  const latestEditSnapshotRef =
    useRef("");

  const historyGuardActiveRef =
    useRef(false);

  const historyGuardTokenRef =
    useRef("");

  const bypassHistoryGuardRef =
    useRef(false);

  const [
    companyId,
    setCompanyId,
  ] =
    useState(
      null
    );

  const [
    companyName,
    setCompanyName,
  ] =
    useState(
      ""
    );

  const [
    industries,
    setIndustries,
  ] =
    useState(
      []
    );

  const [
    carriers,
    setCarriers,
  ] =
    useState(
      []
    );

  const [
    places,
    setPlaces,
  ] =
    useState(
      []
    );

  const initialPickupPoints =
    useMemo(
      () => {
        const points =
          getPickupPoints(
            prefill
          );

        return points.length >
          0
          ? points
          : [
              {
                ...makeEmptyPoint(),

                type:
                  "PICKUP",
              },
            ];
      },
      [
        prefill,
      ]
    );

  const initialDeliveryPoints =
    useMemo(
      () => {
        const points =
          getDeliveryPoints(
            prefill
          );

        return points.length >
          0
          ? points
          : [
              {
                ...makeEmptyPoint(),

                type:
                  "DELIVERY",
              },
            ];
      },
      [
        prefill,
      ]
    );

  const [
    pickupPoints,
    setPickupPoints,
  ] =
    useState(
      initialPickupPoints
    );

  const [
    deliveryPoints,
    setDeliveryPoints,
  ] =
    useState(
      initialDeliveryPoints
    );

  const [
    length,
    setLength,
  ] =
    useState(
      prefill?.length ??
      ""
    );

  const [
    width,
    setWidth,
  ] =
    useState(
      prefill?.width ??
      ""
    );

  const [
    height,
    setHeight,
  ] =
    useState(
      prefill?.height ??
      ""
    );

  const [
    weight,
    setWeight,
  ] =
    useState(
      prefill?.weight ??
      ""
    );

  const [
    merchandise,
    setMerchandise,
  ] =
    useState(
      prefill?.merchandise ??
      ""
    );

  const [
    reference,
    setReference,
  ] =
    useState(
      prefill?.reference ??
      ""
    );

  const [
    price,
    setPrice,
  ] =
    useState(
      prefill?.price ??
      ""
    );

  const [
    priceCurrency,
    setPriceCurrency,
  ] =
    useState(
      prefill?.priceCurrency ??
      "EUR"
    );

  const [
    price4subcontracted,
    setPrice4subcontracted,
  ] =
    useState(
      prefill?.price4subcontracted ??
      prefill?.price4subcontrated ??
      ""
    );

  const [
    notes4driver,
    setNotes4driver,
  ] =
    useState(
      prefill?.notes4driver ??
      ""
    );

  const [
    notes4subcontracted,
    setNotes4subcontracted,
  ] =
    useState(
      prefill?.notes4subcontracted ??
      ""
    );

  const [
    trailerType,
    setTrailerType,
  ] =
    useState(
      prefill?.trailerType ??
      null
    );

  const [
    trailerTypeOther,
    setTrailerTypeOther,
  ] =
    useState(
      prefill?.trailerTypeOther ??
      ""
    );

  const [
    clientType,
    setClientType,
  ] =
    useState(
      prefill?.industryClientId
        ? "industry"
        : prefill?.carrierClientId
          ? "carrier"
          : prefill?.clientType ||
            ""
    );

  const [
    selectedClient,
    setSelectedClient,
  ] =
    useState(
      () => {
        if (
          prefill
            ?.industryClientId
        ) {
          return {
            value:
              prefill.industryClientId,

            label:
              prefill.industryClientName ||
              prefill.industryClient
                ?.companyName ||
              t(
                "fallbacks.client"
              ),
          };
        }

        if (
          prefill
            ?.carrierClientId
        ) {
          return {
            value:
              prefill.carrierClientId,

            label:
              prefill.carrierClientName ||
              prefill.carrierClient
                ?.companyName ||
              t(
                "fallbacks.client"
              ),
          };
        }

        return null;
      }
    );

  const [
    selectedClientContactEmails,
    setSelectedClientContactEmails,
  ] =
    useState(
      () =>
        emailsToOptions(
          prefill?.clientContactEmails
        )
    );

  const [
    transportCarrier,
    setTransportCarrier,
  ] =
    useState(
      () => {
        if (
          prefill?.carrierId
        ) {
          return {
            value:
              String(
                prefill.carrierId
              ),

            label:
              prefill.carrierName ||
              prefill.carrier
                ?.companyName ||
              prefill.loadsAsCarrier
                ?.companyName ||
              t(
                "fallbacks.carrier"
              ),

            isInternal:
              false,
          };
        }

        if (
          prefill
        ) {
          return {
            value:
              "__internal__",

            label:
              prefill.carrierName ||
              t(
                "general.internalCarrier"
              ),

            isInternal:
              true,
          };
        }

        return null;
      }
    );

  const [
    selectedSubcontractedEmails,
    setSelectedSubcontractedEmails,
  ] =
    useState(
      () =>
        emailsToOptions(
          prefill?.subcontractedEmails ??
          prefill?.subcontractedEmail
        )
    );

  const [
    associatedLoadBearer,
    setAssociatedLoadBearer,
  ] =
    useState(
      null
    );

  const [
    loadBearerInfo,
    setLoadBearerInfo,
  ] =
    useState(
      null
    );

  const [
    bearerMinimumLength,
    setBearerMinimumLength,
  ] =
    useState(
      0
    );

  const [
    superimposableAvailableLength,
    setSuperimposableAvailableLength,
  ] =
    useState(
      null
    );

  const [
    superimposableAvailableHeight,
    setSuperimposableAvailableHeight,
  ] =
    useState(
      null
    );

  const [
    merchandiseError,
    setMerchandiseError,
  ] =
    useState(
      ""
    );

  const [
    weightError,
    setWeightError,
  ] =
    useState(
      ""
    );

  const [
    lengthError,
    setLengthError,
  ] =
    useState(
      ""
    );

  const [
    widthError,
    setWidthError,
  ] =
    useState(
      ""
    );

  const [
    heightError,
    setHeightError,
  ] =
    useState(
      ""
    );

  const [
    priceError,
    setPriceError,
  ] =
    useState(
      ""
    );

  const [
    price4subcontractedError,
    setPrice4subcontractedError,
  ] =
    useState(
      ""
    );

  const [
    trailerTypeError,
    setTrailerTypeError,
  ] =
    useState(
      ""
    );

  const [
    trailerTypeOtherError,
    setTrailerTypeOtherError,
  ] =
    useState(
      ""
    );

  const [
    clientTypeError,
    setClientTypeError,
  ] =
    useState(
      ""
    );

  const [
    clientError,
    setClientError,
  ] =
    useState(
      ""
    );

  const [
    transportCarrierError,
    setTransportCarrierError,
  ] =
    useState(
      ""
    );

  const isLoadBearer =
    Boolean(
      load?.loadBearer ??
      prefill?.loadBearer
    );

  const isSpecialLoad =
    Boolean(
      load?.specialLoad ??
      prefill?.specialLoad
    );

  const isSuperImposable =
    Boolean(
      load?.superImposable ??
      prefill?.superImposable
    );

  const primaryMode =
    isSuperImposable
      ? "superImposable"
      : isLoadBearer
        ? "loadBearer"
        : isSpecialLoad
          ? "special"
          : "normal";

  const dimensionLimits =
    isSpecialLoad
      ? SPECIAL_LIMITS
      : NORMAL_LIMITS;

  const clientTypeOptions =
    useMemo(
      () => [
        {
          value:
            "industry",

          label:
            t(
              "enums.clientType.industry"
            ),
        },

        {
          value:
            "carrier",

          label:
            t(
              "enums.clientType.carrier"
            ),
        },
      ],
      [
        t,
      ]
    );

  const loadingAccessOptions =
    useMemo(
      () => [
        {
          value:
            "SIDE",

          label:
            t(
              "loadingAccess.options.side"
            ),
        },

        {
          value:
            "REAR",

          label:
            t(
              "loadingAccess.options.rear"
            ),
        },

        {
          value:
            "TOP",

          label:
            t(
              "loadingAccess.options.top"
            ),
        },

        {
          value:
            "DOCK",

          label:
            t(
              "loadingAccess.options.dock"
            ),
        },

        {
          value:
            "ANY",

          label:
            t(
              "loadingAccess.options.any"
            ),
        },
      ],
      [
        t,
      ]
    );

  const handlingEquipmentOptions =
    useMemo(
      () => [
        {
          value:
            "FORKLIFT",

          label:
            t(
              "handlingEquipment.options.forklift"
            ),
        },

        {
          value:
            "PALLET_JACK",

          label:
            t(
              "handlingEquipment.options.palletJack"
            ),
        },

        {
          value:
            "ELECTRIC_PALLET_JACK",

          label:
            t(
              "handlingEquipment.options.electricPalletJack"
            ),
        },

        {
          value:
            "CRANE",

          label:
            t(
              "handlingEquipment.options.crane"
            ),
        },

        {
          value:
            "LOADING_RAMP",

          label:
            t(
              "handlingEquipment.options.loadingRamp"
            ),
        },

        {
          value:
            "TAIL_LIFT",

          label:
            t(
              "handlingEquipment.options.tailLift"
            ),
        },

        {
          value:
            "HAND_LOAD",

          label:
            t(
              "handlingEquipment.options.handLoad"
            ),
        },

        {
          value:
            "CONVEYOR",

          label:
            t(
              "handlingEquipment.options.conveyor"
            ),
        },

        {
          value:
            "OTHER",

          label:
            t(
              "handlingEquipment.options.other"
            ),
        },
      ],
      [
        t,
      ]
    );

  const trailerTypeOptions =
    useMemo(
      () => [
        {
          value:
            "ANY",

          label:
            t(
              "trailerType.options.ANY"
            ),
        },

        {
          value:
            "TAUTLINER",

          label:
            t(
              "trailerType.options.TAUTLINER"
            ),
        },

        {
          value:
            "REEFER",

          label:
            t(
              "trailerType.options.REEFER"
            ),
        },

        {
          value:
            "BOX",

          label:
            t(
              "trailerType.options.BOX"
            ),
        },

        {
          value:
            "MEGA",

          label:
            t(
              "trailerType.options.MEGA"
            ),
        },

        {
          value:
            "FLATBED",

          label:
            t(
              "trailerType.options.FLATBED"
            ),
        },

        {
          value:
            "TIPPER",

          label:
            t(
              "trailerType.options.TIPPER"
            ),
        },

        {
          value:
            "LOWBED",

          label:
            t(
              "trailerType.options.LOWBED"
            ),
        },

        {
          value:
            "TANKER",

          label:
            t(
              "trailerType.options.TANKER"
            ),
        },

        {
          value:
            "CONTAINER_CHASSIS",

          label:
            t(
              "trailerType.options.CONTAINER_CHASSIS"
            ),
        },

        {
          value:
            "OTHER",

          label:
            t(
              "trailerType.options.OTHER"
            ),
        },
      ],
      [
        t,
      ]
    );

  const industryOptions =
    useMemo(
      () =>
        industries
          .map(
            (
              industry
            ) => ({
              value:
                industry.id,

              label:
                industry.companyName ||
                industry.name ||
                "",

              raw:
                industry,
            })
          )
          .filter(
            (
              option
            ) =>
              option.label
          )
          .sort(
            (
              a,
              b
            ) =>
              a.label.localeCompare(
                b.label,
                undefined,
                {
                  sensitivity:
                    "base",
                }
              )
          ),
      [
        industries,
      ]
    );

  const carrierClientOptions =
    useMemo(
      () =>
        carriers
          .map(
            (
              carrier
            ) => ({
              value:
                carrier.id,

              label:
                carrier.companyName ||
                carrier.name ||
                carrier.company
                  ?.companyName ||
                "",

              raw:
                carrier,
            })
          )
          .filter(
            (
              option
            ) =>
              option.label
          )
          .sort(
            (
              a,
              b
            ) =>
              a.label.localeCompare(
                b.label,
                undefined,
                {
                  sensitivity:
                    "base",
                }
              )
          ),
      [
        carriers,
      ]
    );

  const internalCarrierOption =
    useMemo(
      () => ({
        value:
          "__internal__",

        label:
          companyName
            ? t(
                "general.internalCarrierWithCompany",
                {
                  companyName,
                }
              )
            : t(
                "general.internalCarrier"
              ),

        isInternal:
          true,
      }),
      [
        companyName,
        t,
      ]
    );

  const transportCarrierOptions =
    useMemo(
      () => [
        internalCarrierOption,

        ...carrierClientOptions.map(
          (
            option
          ) => ({
            ...option,

            value:
              String(
                option.value
              ),

            isInternal:
              false,
          })
        ),
      ],
      [
        carrierClientOptions,
        internalCarrierOption,
      ]
    );

  const clientOptions =
    clientType ===
    "industry"
      ? industryOptions
      : clientType ===
          "carrier"
        ? carrierClientOptions
        : [];

  const selectedClientContactEmailOptions =
    useMemo(
      () => {
        if (
          !selectedClient?.value
        ) {
          return [];
        }

        const source =
          clientType ===
          "industry"
            ? industries
            : clientType ===
                "carrier"
              ? carriers
              : [];

        const selected =
          source.find(
            (
              item
            ) =>
              String(
                item.id
              ) ===
              String(
                selectedClient.value
              )
          );

        return extractClientEmails(
          selected
        );
      },
      [
        carriers,
        clientType,
        industries,
        selectedClient,
      ]
    );

  const selectedSubcontractedEmailOptions =
    useMemo(
      () => {
        if (
          transportCarrier
            ?.isInternal ||
          !transportCarrier
            ?.value
        ) {
          return [];
        }

        const selected =
          carriers.find(
            (
              carrier
            ) =>
              String(
                carrier.id
              ) ===
              String(
                transportCarrier.value
              )
          );

        return extractClientEmails(
          selected
        );
      },
      [
        carriers,
        transportCarrier,
      ]
    );

  const placeOptions =
    useMemo(
      () =>
        places
          .map(
            (
              place
            ) => ({
              value:
                place.id,

              label:
                place.placeName ||
                place.name ||
                "",

              metaCity:
                place.city ||
                "",

              metaCountry:
                place.country ||
                "",

              fullData:
                place,
            })
          )
          .filter(
            (
              option
            ) =>
              option.label
          )
          .sort(
            (
              a,
              b
            ) =>
              a.label.localeCompare(
                b.label,
                undefined,
                {
                  sensitivity:
                    "base",
                }
              )
          ),
      [
        places,
      ]
    );

  const showSubcontractedFields =
    Boolean(
      transportCarrier
        ?.value &&
      !transportCarrier
        ?.isInternal
    );

  const pickupTotals =
    useMemo(
      () =>
        calculatePointTotals(
          pickupPoints
        ),
      [
        pickupPoints,
      ]
    );

  const deliveryTotals =
    useMemo(
      () =>
        calculatePointTotals(
          deliveryPoints
        ),
      [
        deliveryPoints,
      ]
    );

  const currentEditSnapshot =
    useMemo(
      () =>
        JSON.stringify({
          pickupPoints:
            pickupPoints.map(
              (
                point,
                index
              ) => ({
                ...sanitizePoint(
                  point
                ),

                sequence:
                  index,
              })
            ),

          deliveryPoints:
            deliveryPoints.map(
              (
                point,
                index
              ) => ({
                ...sanitizePoint(
                  point
                ),

                sequence:
                  index,
              })
            ),

          length:
            toNumberOrNull(
              length
            ),

          width:
            toNumberOrNull(
              width
            ),

          height:
            toNumberOrNull(
              height
            ),

          weight:
            toNumberOrNull(
              weight
            ),

          merchandise:
            normalizeText(
              merchandise
            ),

          reference:
            normalizeText(
              reference
            ),

          price:
            toNumberOrNull(
              price
            ),

          priceCurrency:
            String(
              priceCurrency ||
              "EUR"
            ),

          price4subcontracted:
            showSubcontractedFields
              ? toNumberOrNull(
                  price4subcontracted
                )
              : null,

          notes4driver:
            normalizeText(
              notes4driver
            ),

          notes4subcontracted:
            showSubcontractedFields
              ? normalizeText(
                  notes4subcontracted
                )
              : "",

          trailerType:
            String(
              trailerType ||
              ""
            )
              .trim()
              .toUpperCase(),

          trailerTypeOther:
            normalizeText(
              trailerTypeOther
            ),

          clientType:
            clientType ||
            "",

          selectedClientId:
            selectedClient
              ?.value ??
            null,

          clientContactEmails:
            selectedClientContactEmails.map(
              (
                option
              ) =>
                String(
                  option?.value ||
                  ""
                )
                  .trim()
                  .toLowerCase()
            ),

          transportCarrier: {
            value:
              transportCarrier
                ?.value ??
              null,

            isInternal:
              Boolean(
                transportCarrier
                  ?.isInternal
              ),
          },

          subcontractedEmails:
            showSubcontractedFields
              ? selectedSubcontractedEmails.map(
                  (
                    option
                  ) =>
                    String(
                      option?.value ||
                      ""
                    )
                      .trim()
                      .toLowerCase()
                )
              : [],
        }),
      [
        clientType,
        deliveryPoints,
        height,
        length,
        merchandise,
        notes4driver,
        notes4subcontracted,
        pickupPoints,
        price,
        price4subcontracted,
        priceCurrency,
        reference,
        selectedClient,
        selectedClientContactEmails,
        selectedSubcontractedEmails,
        showSubcontractedFields,
        trailerType,
        trailerTypeOther,
        transportCarrier,
        weight,
        width,
      ]
    );

  latestEditSnapshotRef.current =
    currentEditSnapshot;

  const hasUnsavedChanges =
    savedEditSnapshot !==
      null &&
    currentEditSnapshot !==
      savedEditSnapshot;

  const loadNumber =
    load?.loadNumber ??
    load?.uiCounter ??
    prefill?.loadNumber ??
    prefill?.uiCounter ??
    load?.id ??
    loadId;

  const transportSessionNumber =
    load?.transportSession
      ?.transportSessionNumber ??
    load?.transportSessionNumber ??
    load?.tsUiCounter ??
    prefill?.transportSession
      ?.transportSessionNumber ??
    prefill?.tsUiCounter ??
    null;

  const hydrateFromLoad =
    useCallback(
      (
        data
      ) => {
        if (
          !data ||
          typeof data !==
            "object"
        ) {
          return;
        }

        setLoad(
          (
            current
          ) =>
            mergeLoadData(
              current,
              data
            )
        );

        setLength(
          data.length ??
          ""
        );

        setWidth(
          data.width ??
          ""
        );

        setHeight(
          data.height ??
          ""
        );

        setWeight(
          data.weight ??
          ""
        );

        setMerchandise(
          data.merchandise ??
          ""
        );

        setReference(
          data.reference ??
          ""
        );

        setPrice(
          data.price ??
          ""
        );

        setPriceCurrency(
          data.priceCurrency ??
          "EUR"
        );

        setPrice4subcontracted(
          data.price4subcontracted ??
          data.price4subcontrated ??
          ""
        );

        setNotes4driver(
          data.notes4driver ??
          ""
        );

        setNotes4subcontracted(
          data.notes4subcontracted ??
          ""
        );

        setTrailerType(
          data.trailerType ??
          null
        );

        setTrailerTypeOther(
          data.trailerTypeOther ??
          ""
        );

        const nextPickupPoints =
          getPickupPoints(
            data
          );

        const nextDeliveryPoints =
          getDeliveryPoints(
            data
          );

        setPickupPoints(
          nextPickupPoints.length >
            0
            ? nextPickupPoints
            : [
                {
                  ...makeEmptyPoint(),

                  type:
                    "PICKUP",
                },
              ]
        );

        setDeliveryPoints(
          nextDeliveryPoints.length >
            0
            ? nextDeliveryPoints
            : [
                {
                  ...makeEmptyPoint(),

                  type:
                    "DELIVERY",
                },
              ]
        );

        if (
          data.industryClientId
        ) {
          setClientType(
            "industry"
          );

          setSelectedClient({
            value:
              data.industryClientId,

            label:
              data.industryClientName ||
              data.industryClient
                ?.companyName ||
              t(
                "fallbacks.client"
              ),
          });
        } else if (
          data.carrierClientId
        ) {
          setClientType(
            "carrier"
          );

          setSelectedClient({
            value:
              data.carrierClientId,

            label:
              data.carrierClientName ||
              data.carrierClient
                ?.companyName ||
              t(
                "fallbacks.client"
              ),
          });
        } else {
          setClientType(
            data.clientType ||
            ""
          );

          setSelectedClient(
            null
          );
        }

        setSelectedClientContactEmails(
          emailsToOptions(
            data.clientContactEmails
          )
        );

        setSelectedSubcontractedEmails(
          emailsToOptions(
            data.subcontractedEmails ??
            data.subcontractedEmail
          )
        );

        if (
          data.carrierId !==
            null &&
          data.carrierId !==
            undefined
        ) {
          setTransportCarrier({
            value:
              String(
                data.carrierId
              ),

            label:
              data.carrierName ||
              data.carrier
                ?.companyName ||
              data.loadsAsCarrier
                ?.companyName ||
              t(
                "fallbacks.carrier"
              ),

            isInternal:
              false,
          });
        } else {
          setTransportCarrier(
            internalCarrierOption
          );
        }
      },
      [
        internalCarrierOption,
        t,
      ]
    );

  useEffect(
    () => {
      let alive =
        true;

      const loadLists =
        async () => {
          const results =
            await Promise.allSettled([
              axios.get(
                "/auth/whoami"
              ),

              axios.get(
                "/industries"
              ),

              axios.get(
                "/carriers"
              ),

              axios.get(
                "/places"
              ),
            ]);

          if (
            !alive
          ) {
            return;
          }

          const [
            whoamiResult,
            industriesResult,
            carriersResult,
            placesResult,
          ] =
            results;

          if (
            whoamiResult.status ===
            "fulfilled"
          ) {
            const data =
              whoamiResult
                .value
                .data ||
              {};

            const nextCompanyId =
              data.companyId ??
              data.company_id ??
              data.company?.id ??
              null;

            const nextCompanyName =
              data.company_name ??
              data.companyName ??
              data.company?.name ??
              data.company?.companyName ??
              "";

            setCompanyId(
              nextCompanyId
            );

            setCompanyName(
              String(
                nextCompanyName ||
                  ""
              ).trim()
            );
          }

          if (
            industriesResult.status ===
            "fulfilled"
          ) {
            setIndustries(
              Array.isArray(
                industriesResult
                  .value
                  .data
              )
                ? industriesResult
                    .value
                    .data
                : []
            );
          }

          if (
            carriersResult.status ===
            "fulfilled"
          ) {
            setCarriers(
              Array.isArray(
                carriersResult
                  .value
                  .data
              )
                ? carriersResult
                    .value
                    .data
                : []
            );
          }

          if (
            placesResult.status ===
            "fulfilled"
          ) {
            setPlaces(
              Array.isArray(
                placesResult
                  .value
                  .data
              )
                ? placesResult
                    .value
                    .data
                : []
            );
          }
        };

      void loadLists();

      return () => {
        alive =
          false;
      };
    },
    []
  );

  useEffect(
    () => {
      if (
        String(
          transportCarrier
            ?.value ||
            ""
        ) !==
        "__internal__"
      ) {
        return;
      }

      setTransportCarrier(
        internalCarrierOption
      );
    },
    [
      internalCarrierOption,
      transportCarrier?.value,
    ]
  );

  useEffect(
    () => {
      if (
        showSubcontractedFields
      ) {
        return;
      }

      setPrice4subcontracted(
        ""
      );

      setPrice4subcontractedError(
        ""
      );

      setNotes4subcontracted(
        ""
      );

      setSelectedSubcontractedEmails(
        []
      );
    },
    [
      showSubcontractedFields,
    ]
  );

  useEffect(
    () => {
      let alive =
        true;

      const fetchLoad =
        async () => {
          setFetching(
            true
          );

          try {
            const [
              editResponse,
              detailResponse,
            ] =
              await Promise.allSettled([
                axios.get(
                  `/loads/${loadId}/editloads`
                ),

                axios.get(
                  `/loads/${loadId}`
                ),
              ]);

            if (
              !alive
            ) {
              return;
            }

            const editData =
              editResponse.status ===
              "fulfilled"
                ? editResponse
                    .value
                    .data ||
                  {}
                : {};

            const detailData =
              detailResponse.status ===
              "fulfilled"
                ? detailResponse
                    .value
                    .data ||
                  {}
                : {};

            if (
              editResponse.status !==
                "fulfilled" &&
              detailResponse.status !==
                "fulfilled"
            ) {
              throw new Error(
                t(
                  "errors.fetchFailed"
                )
              );
            }

            const merged = {
              ...detailData,
              ...editData,

              pickupDeliveries:
                editData.pickupDeliveries ??
                detailData.pickupDeliveries,

              pickupLocations:
                editData.pickupLocations ??
                detailData.pickupLocations,

              deliveryLocations:
                editData.deliveryLocations ??
                detailData.deliveryLocations,
            };

            hydrateFromLoad(
              merged
            );

            setError(
              ""
            );
          } catch (
            requestError
          ) {
            console.error(
              "[EditLoad] failed to fetch load:",
              requestError
            );

            if (
              alive
            ) {
              setError(
                t(
                  "errors.fetchFailed"
                )
              );
            }
          } finally {
            if (
              alive
            ) {
              setReady(
                true
              );

              setFetching(
                false
              );
            }
          }
        };

      void fetchLoad();

      return () => {
        alive =
          false;
      };
    },
    [
      hydrateFromLoad,
      loadId,
      t,
    ]
  );

  useEffect(
    () => {
      if (
        !isSuperImposable ||
        !load?.associatedLoadBearerId
      ) {
        setAssociatedLoadBearer(
          null
        );

        setSuperimposableAvailableLength(
          null
        );

        setSuperimposableAvailableHeight(
          null
        );

        return;
      }

      let alive =
        true;

      const fetchBearer =
        async () => {
          try {
            const bearerResponse =
              await axios.get(
                `/loads/${load.associatedLoadBearerId}`
              );

            if (
              !alive
            ) {
              return;
            }

            const bearer =
              bearerResponse.data ||
              null;

            setAssociatedLoadBearer(
              bearer
            );

            const bearerLength =
              toNumberOrNull(
                bearer?.length
              );

            const bearerHeight =
              toNumberOrNull(
                bearer?.height
              );

            let availableLength =
              bearerLength;

            try {
              const sessionId =
                load.transportsessionId ??
                load.transportSessionId ??
                bearer?.transportsessionId ??
                bearer?.transportSessionId;

              if (
                sessionId
              ) {
                const loadsResponse =
                  await axios.get(
                    "/loads"
                  );

                const allLoads =
                  Array.isArray(
                    loadsResponse.data
                  )
                    ? loadsResponse.data
                    : [];

                const otherSuperimposableLength =
                  allLoads
                    .filter(
                      (
                        candidate
                      ) =>
                        Number(
                          candidate?.id
                        ) !==
                          Number(
                            loadId
                          ) &&
                        Boolean(
                          candidate?.superImposable
                        ) &&
                        Number(
                          candidate
                            ?.associatedLoadBearerId
                        ) ===
                          Number(
                            load.associatedLoadBearerId
                          )
                    )
                    .reduce(
                      (
                        total,
                        candidate
                      ) =>
                        total +
                        (
                          toNumberOrNull(
                            candidate?.length
                          ) ??
                          0
                        ),
                      0
                    );

                if (
                  bearerLength !==
                  null
                ) {
                  availableLength =
                    Math.max(
                      0,
                      bearerLength -
                        otherSuperimposableLength
                    );
                }
              }
            } catch (
              calculationError
            ) {
              console.warn(
                "[EditLoad] failed to calculate superimposable remaining length:",
                calculationError
              );
            }

            setSuperimposableAvailableLength(
              availableLength
            );

            setSuperimposableAvailableHeight(
              bearerHeight !==
                null
                ? Math.max(
                    0,
                    NORMAL_LIMITS.height -
                      bearerHeight
                  )
                : null
            );
          } catch (
            requestError
          ) {
            console.warn(
              "[EditLoad] failed to load associated load bearer:",
              requestError
            );

            if (
              alive
            ) {
              setAssociatedLoadBearer(
                null
              );
            }
          }
        };

      void fetchBearer();

      return () => {
        alive =
          false;
      };
    },
    [
      isSuperImposable,
      load?.associatedLoadBearerId,
      load?.transportSessionId,
      load?.transportsessionId,
      loadId,
    ]
  );

  useEffect(
    () => {
      if (
        !isLoadBearer
      ) {
        setBearerMinimumLength(
          0
        );

        setLoadBearerInfo(
          null
        );

        return;
      }

      let alive =
        true;

      const calculateBearerRules =
        async () => {
          try {
            const loadsResponse =
              await axios.get(
                "/loads"
              );

            if (
              !alive
            ) {
              return;
            }

            const allLoads =
              Array.isArray(
                loadsResponse.data
              )
                ? loadsResponse.data
                : [];

            const children =
              allLoads.filter(
                (
                  candidate
                ) =>
                  Boolean(
                    candidate
                      ?.superImposable
                  ) &&
                  Number(
                    candidate
                      ?.associatedLoadBearerId
                  ) ===
                    Number(
                      loadId
                    )
              );

            const minimumLength =
              children.reduce(
                (
                  total,
                  candidate
                ) =>
                  total +
                  (
                    toNumberOrNull(
                      candidate?.length
                    ) ??
                    0
                  ),
                0
              );

            setBearerMinimumLength(
              minimumLength
            );

            setLoadBearerInfo({
              childrenCount:
                children.length,

              children,
            });
          } catch (
            requestError
          ) {
            console.warn(
              "[EditLoad] failed to calculate load bearer rules:",
              requestError
            );
          }
        };

      void calculateBearerRules();

      return () => {
        alive =
          false;
      };
    },
    [
      isLoadBearer,
      loadId,
    ]
  );

  useEffect(
    () => {
      const pickupLength =
        pickupTotals.length;

      const deliveryLength =
        deliveryTotals.length;

      const nextLength =
        pickupLength >
        0
          ? pickupLength
          : deliveryLength;

      if (
        nextLength >
        0
      ) {
        setLength(
          nextLength.toFixed(
            2
          )
        );
      }

      const nextWidth =
        Math.max(
          pickupTotals.width,
          deliveryTotals.width
        );

      if (
        nextWidth >
        0
      ) {
        setWidth(
          nextWidth.toFixed(
            2
          )
        );
      }

      const nextHeight =
        Math.max(
          pickupTotals.height,
          deliveryTotals.height
        );

      if (
        nextHeight >
        0
      ) {
        setHeight(
          nextHeight.toFixed(
            2
          )
        );
      }
    },
    [
      deliveryTotals.height,
      deliveryTotals.length,
      deliveryTotals.width,
      pickupTotals.height,
      pickupTotals.length,
      pickupTotals.width,
    ]
  );

  useEffect(
    () => {
      if (
        !ready ||
        fetching ||
        savedEditSnapshot !==
          null
      ) {
        return undefined;
      }

      const timeoutId =
        window.setTimeout(
          () => {
            setSavedEditSnapshot(
              latestEditSnapshotRef.current
            );
          },
          0
        );

      return () => {
        window.clearTimeout(
          timeoutId
        );
      };
    },
    [
      currentEditSnapshot,
      fetching,
      ready,
      savedEditSnapshot,
    ]
  );

  useEffect(
    () => {
      if (
        !hasUnsavedChanges
      ) {
        return undefined;
      }

      const handleBeforeUnload =
        (
          event
        ) => {
          event.preventDefault();

          event.returnValue =
            "";
        };

      window.addEventListener(
        "beforeunload",
        handleBeforeUnload
      );

      return () => {
        window.removeEventListener(
          "beforeunload",
          handleBeforeUnload
        );
      };
    },
    [
      hasUnsavedChanges,
    ]
  );

  useEffect(
    () => {
      if (
        hasUnsavedChanges
      ) {
        if (
          historyGuardActiveRef.current
        ) {
          return;
        }

        const guardToken =
          `edit-load-${loadId}-${Date.now()}`;

        historyGuardTokenRef.current =
          guardToken;

        window.history.pushState(
          {
            ...(
              window.history
                .state ||
              {}
            ),

            __loadevoEditLoadGuard:
              guardToken,
          },
          "",
          window.location.href
        );

        historyGuardActiveRef.current =
          true;

        return;
      }

      if (
        !historyGuardActiveRef.current
      ) {
        return;
      }

      const currentGuardToken =
        window.history
          .state
          ?.__loadevoEditLoadGuard;

      if (
        currentGuardToken ===
        historyGuardTokenRef.current
      ) {
        bypassHistoryGuardRef.current =
          true;

        historyGuardActiveRef.current =
          false;

        window.history.back();

        window.setTimeout(
          () => {
            bypassHistoryGuardRef.current =
              false;
          },
          50
        );

        return;
      }

      historyGuardActiveRef.current =
        false;
    },
    [
      hasUnsavedChanges,
      loadId,
    ]
  );

  useEffect(
    () => {
      const handlePopState =
        (
          event
        ) => {
          if (
            !hasUnsavedChanges ||
            !historyGuardActiveRef.current ||
            bypassHistoryGuardRef.current
          ) {
            return;
          }

          const targetGuardToken =
            event.state
              ?.__loadevoEditLoadGuard;

          if (
            targetGuardToken ===
            historyGuardTokenRef.current
          ) {
            return;
          }

          const shouldLeave =
            window.confirm(
              t(
                "workspace.unsavedChanges.browserWarning"
              )
            );

          if (
            shouldLeave
          ) {
            bypassHistoryGuardRef.current =
              true;

            historyGuardActiveRef.current =
              false;

            window.history.back();

            window.setTimeout(
              () => {
                bypassHistoryGuardRef.current =
                  false;
              },
              50
            );

            return;
          }

          bypassHistoryGuardRef.current =
            true;

          window.history.forward();

          window.setTimeout(
            () => {
              bypassHistoryGuardRef.current =
                false;
            },
            50
          );
        };

      window.addEventListener(
        "popstate",
        handlePopState
      );

      return () => {
        window.removeEventListener(
          "popstate",
          handlePopState
        );
      };
    },
    [
      hasUnsavedChanges,
      t,
    ]
  );

  useEffect(
    () => {
      if (
        !hasUnsavedChanges
      ) {
        return undefined;
      }

      const handleDocumentClick =
        (
          event
        ) => {
          if (
            event.defaultPrevented ||
            event.button !==
              0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }

          const target =
            event.target;

          const anchor =
            target
              ?.closest
              ? target.closest(
                  "a[href]"
                )
              : null;

          if (
            !anchor
          ) {
            return;
          }

          if (
            anchor.target ===
              "_blank" ||
            anchor.hasAttribute(
              "download"
            )
          ) {
            return;
          }

          const rawHref =
            anchor.getAttribute(
              "href"
            );

          if (
            !rawHref ||
            rawHref.startsWith(
              "#"
            )
          ) {
            return;
          }

          let targetUrl;

          try {
            targetUrl =
              new URL(
                anchor.href,
                window.location.href
              );
          } catch {
            return;
          }

          const currentUrl =
            new URL(
              window.location.href
            );

          if (
            targetUrl.href ===
            currentUrl.href
          ) {
            return;
          }

          event.preventDefault();

          event.stopPropagation();

          setPendingNavigation({
            type:
              targetUrl.origin ===
              currentUrl.origin
                ? "internal"
                : "external",

            href:
              targetUrl.href,
          });

          setUnsavedChangesModalOpen(
            true
          );
        };

      document.addEventListener(
        "click",
        handleDocumentClick,
        true
      );

      return () => {
        document.removeEventListener(
          "click",
          handleDocumentClick,
          true
        );
      };
    },
    [
      hasUnsavedChanges,
    ]
  );

  const validatePoint =
    useCallback(
      (
        point
      ) => {
        const nextPoint = {
          ...point,

          locationError:
            !normalizeText(
              point.location
            ),

          dateError:
            !normalizeText(
              point.date
            ),

          hourError:
            !/^([01]\d|2[0-3]):([0-5]\d)$/.test(
              normalizeText(
                point.hour
              )
            ),

          lengthError:
            "",

          widthError:
            "",

          heightError:
            "",

          handlingEquipmentOtherError:
            false,
        };

        const pointLength =
          toNumberOrNull(
            point.length
          );

        const pointWidth =
          toNumberOrNull(
            point.width
          );

        const pointHeight =
          toNumberOrNull(
            point.height
          );

        if (
          pointLength !==
            null &&
          pointLength <=
            0
        ) {
          nextPoint.lengthError =
            t(
              "errors.positiveNumber"
            );
        }

        if (
          pointWidth !==
            null &&
          pointWidth <=
            0
        ) {
          nextPoint.widthError =
            t(
              "errors.positiveNumber"
            );
        }

        if (
          pointHeight !==
            null &&
          pointHeight <=
            0
        ) {
          nextPoint.heightError =
            t(
              "errors.positiveNumber"
            );
        }

        if (
          pointLength !==
            null &&
          pointLength >
            dimensionLimits.length
        ) {
          nextPoint.lengthError =
            isSpecialLoad
              ? t(
                  "errors.specialLengthMax"
                )
              : t(
                  "errors.lengthMax"
                );
        }

        if (
          pointWidth !==
            null &&
          pointWidth >
            dimensionLimits.width
        ) {
          nextPoint.widthError =
            isSpecialLoad
              ? t(
                  "errors.specialWidthMax"
                )
              : t(
                  "errors.widthMax"
                );
        }

        if (
          pointHeight !==
            null &&
          pointHeight >
            dimensionLimits.height
        ) {
          nextPoint.heightError =
            isSpecialLoad
              ? t(
                  "errors.specialHeightMax"
                )
              : t(
                  "errors.heightMax"
                );
        }

        if (
          String(
            point.handlingEquipment ||
              ""
          )
            .trim()
            .toUpperCase() ===
            "OTHER" &&
          !normalizeText(
            point.handlingEquipmentOther
          )
        ) {
          nextPoint.handlingEquipmentOtherError =
            true;
        }

        return nextPoint;
      },
      [
        dimensionLimits.height,
        dimensionLimits.length,
        dimensionLimits.width,
        isSpecialLoad,
        t,
      ]
    );

  const handleAddPickup =
    () => {
      if (
        pickupPoints.length >=
        MAX_POINTS
      ) {
        setError(
          t(
            "errors.maxPickups",
            {
              max:
                MAX_POINTS,
            }
          )
        );

        return;
      }

      setPickupPoints(
        (
          current
        ) => [
          ...current,

          {
            ...makeEmptyPoint(),

            type:
              "PICKUP",
          },
        ]
      );
    };

  const handleAddDelivery =
    () => {
      if (
        deliveryPoints.length >=
        MAX_POINTS
      ) {
        setError(
          t(
            "errors.maxDeliveries",
            {
              max:
                MAX_POINTS,
            }
          )
        );

        return;
      }

      setDeliveryPoints(
        (
          current
        ) => [
          ...current,

          {
            ...makeEmptyPoint(),

            type:
              "DELIVERY",
          },
        ]
      );
    };

  const handleRemovePickup =
    (
      index
    ) => {
      setPickupPoints(
        (
          current
        ) => {
          const next =
            current.filter(
              (
                _,
                pointIndex
              ) =>
                pointIndex !==
                index
            );

          return next.length >
            0
            ? next
            : [
                {
                  ...makeEmptyPoint(),

                  type:
                    "PICKUP",
                },
              ];
        }
      );
    };

  const handleRemoveDelivery =
    (
      index
    ) => {
      setDeliveryPoints(
        (
          current
        ) => {
          const next =
            current.filter(
              (
                _,
                pointIndex
              ) =>
                pointIndex !==
                index
            );

          return next.length >
            0
            ? next
            : [
                {
                  ...makeEmptyPoint(),

                  type:
                    "DELIVERY",
                },
              ];
        }
      );
    };

  const resetErrors =
    () => {
      setError(
        ""
      );

      setMerchandiseError(
        ""
      );

      setWeightError(
        ""
      );

      setLengthError(
        ""
      );

      setWidthError(
        ""
      );

      setHeightError(
        ""
      );

      setPriceError(
        ""
      );

      setPrice4subcontractedError(
        ""
      );

      setTrailerTypeError(
        ""
      );

      setTrailerTypeOtherError(
        ""
      );

      setClientTypeError(
        ""
      );

      setClientError(
        ""
      );

      setTransportCarrierError(
        ""
      );
    };

  const validateForm =
    (
      pointOverrides = {}
    ) => {
      resetErrors();

      let valid =
        true;

      let generalMessage =
        "";

      const pickupPointsForValidation =
        Array.isArray(
          pointOverrides.pickupPoints
        )
          ? pointOverrides.pickupPoints
          : pickupPoints;

      const deliveryPointsForValidation =
        Array.isArray(
          pointOverrides.deliveryPoints
        )
          ? pointOverrides.deliveryPoints
          : deliveryPoints;

      const numericLength =
        toNumberOrNull(
          length
        );

      const numericWidth =
        toNumberOrNull(
          width
        );

      const numericHeight =
        toNumberOrNull(
          height
        );

      const numericWeight =
        toNumberOrNull(
          weight
        );

      const numericPrice =
        toNumberOrNull(
          price
        );

      if (
        !normalizeText(
          merchandise
        )
      ) {
        setMerchandiseError(
          t(
            "errors.merchandiseRequired"
          )
        );

        valid =
          false;
      }

      if (
        numericWeight ===
          null ||
        numericWeight <=
          0
      ) {
        setWeightError(
          t(
            "errors.weightRequired"
          )
        );

        valid =
          false;
      }

      if (
        numericLength ===
          null ||
        numericLength <=
          0
      ) {
        setLengthError(
          t(
            "errors.lengthRequired"
          )
        );

        valid =
          false;
      } else if (
        numericLength >
        dimensionLimits.length
      ) {
        setLengthError(
          isSpecialLoad
            ? t(
                "errors.specialLengthMax"
              )
            : t(
                "errors.lengthMax"
              )
        );

        valid =
          false;
      }

      if (
        numericWidth ===
          null ||
        numericWidth <=
          0
      ) {
        setWidthError(
          t(
            "errors.widthRequired"
          )
        );

        valid =
          false;
      } else if (
        numericWidth >
        dimensionLimits.width
      ) {
        setWidthError(
          isSpecialLoad
            ? t(
                "errors.specialWidthMax"
              )
            : t(
                "errors.widthMax"
              )
        );

        valid =
          false;
      }

      if (
        numericHeight ===
          null ||
        numericHeight <=
          0
      ) {
        setHeightError(
          t(
            "errors.heightRequired"
          )
        );

        valid =
          false;
      } else if (
        numericHeight >
        dimensionLimits.height
      ) {
        setHeightError(
          isSpecialLoad
            ? t(
                "errors.specialHeightMax"
              )
            : t(
                "errors.heightMax"
              )
        );

        valid =
          false;
      }

      if (
        numericPrice ===
          null ||
        numericPrice <
          0
      ) {
        setPriceError(
          t(
            "errors.invalidPrice"
          )
        );

        valid =
          false;
      }

      if (
        showSubcontractedFields &&
        normalizeText(
          price4subcontracted
        )
      ) {
        const numericSubcontractedPrice =
          toNumberOrNull(
            price4subcontracted
          );

        if (
          numericSubcontractedPrice ===
            null ||
          numericSubcontractedPrice <
            0
        ) {
          setPrice4subcontractedError(
            t(
              "errors.invalidPrice"
            )
          );

          valid =
            false;
        }
      }

      if (
        !clientType
      ) {
        setClientTypeError(
          t(
            "errors.clientTypeRequired"
          )
        );

        valid =
          false;
      }

      if (
        !selectedClient
          ?.value
      ) {
        setClientError(
          t(
            "errors.clientRequired"
          )
        );

        valid =
          false;
      }

      if (
        !transportCarrier
          ?.value
      ) {
        setTransportCarrierError(
          t(
            "errors.transportCarrierRequired"
          )
        );

        valid =
          false;
      }

      if (
        !trailerType
      ) {
        setTrailerTypeError(
          t(
            "errors.trailerTypeRequired"
          )
        );

        valid =
          false;
      }

      if (
        String(
          trailerType ||
            ""
        )
          .trim()
          .toUpperCase() ===
          "OTHER" &&
        !normalizeText(
          trailerTypeOther
        )
      ) {
        setTrailerTypeOtherError(
          t(
            "errors.trailerTypeOtherRequired"
          )
        );

        valid =
          false;
      }

      if (
        isSpecialLoad &&
        numericLength !==
          null &&
        numericWidth !==
          null &&
        numericHeight !==
          null
      ) {
        const hasSpecialDimension =
          numericLength >=
            SPECIAL_THRESHOLD.length ||
          numericWidth >=
            SPECIAL_THRESHOLD.width ||
          numericHeight >=
            SPECIAL_THRESHOLD.height;

        if (
          !hasSpecialDimension
        ) {
          generalMessage =
            t(
              "errors.specialNeedsOneDimension"
            );

          valid =
            false;
        }
      }

      if (
        isLoadBearer &&
        numericLength !==
          null &&
        numericLength <
          bearerMinimumLength
      ) {
        generalMessage =
          t(
            "errors.loadBearerTooShort",
            {
              minimum:
                bearerMinimumLength.toFixed(
                  2
                ),
            }
          );

        valid =
          false;
      }

      if (
        isSuperImposable &&
        superimposableAvailableLength !==
          null &&
        numericLength !==
          null &&
        numericLength >
          superimposableAvailableLength
      ) {
        generalMessage =
          t(
            "errors.superimposableLengthExceedsBearer",
            {
              available:
                Number(
                  superimposableAvailableLength
                ).toFixed(
                  2
                ),
            }
          );

        valid =
          false;
      }

      if (
        isSuperImposable &&
        superimposableAvailableHeight !==
          null &&
        numericHeight !==
          null &&
        numericHeight >
          superimposableAvailableHeight
      ) {
        generalMessage =
          t(
            "errors.superimposableHeightExceedsBearer",
            {
              available:
                Number(
                  superimposableAvailableHeight
                ).toFixed(
                  2
                ),
            }
          );

        valid =
          false;
      }

      const validatedPickups =
        pickupPointsForValidation.map(
          validatePoint
        );

      const validatedDeliveries =
        deliveryPointsForValidation.map(
          validatePoint
        );

      const pickupTotalsForValidation =
        calculatePointTotals(
          validatedPickups
        );

      const deliveryTotalsForValidation =
        calculatePointTotals(
          validatedDeliveries
        );

      setPickupPoints(
        validatedPickups
      );

      setDeliveryPoints(
        validatedDeliveries
      );

      const invalidPoint =
        [
          ...validatedPickups,
          ...validatedDeliveries,
        ].some(
          (
            point
          ) =>
            point.locationError ||
            point.dateError ||
            point.hourError ||
            point.lengthError ||
            point.widthError ||
            point.heightError ||
            point.handlingEquipmentOtherError
        );

      if (
        invalidPoint
      ) {
        generalMessage =
          generalMessage ||
          t(
            "errors.invalidStopFields"
          );

        valid =
          false;
      }

      const pickupTimestamps =
        validatedPickups
          .map(
            getTimestamp
          )
          .filter(
            (
              timestamp
            ) =>
              timestamp !==
              null
          );

      const deliveryTimestamps =
        validatedDeliveries
          .map(
            getTimestamp
          )
          .filter(
            (
              timestamp
            ) =>
              timestamp !==
              null
          );

      if (
        pickupTimestamps.length >
          0 &&
        deliveryTimestamps.length >
          0
      ) {
        const latestPickup =
          Math.max(
            ...pickupTimestamps
          );

        const earliestDelivery =
          Math.min(
            ...deliveryTimestamps
          );

        if (
          earliestDelivery <
          latestPickup
        ) {
          generalMessage =
            t(
              "errors.deliveryBeforePickup"
            );

          valid =
            false;
        }
      }

      if (
        pickupTotalsForValidation.length >
          0 &&
        deliveryTotalsForValidation.length >
          0 &&
        Math.abs(
          pickupTotalsForValidation.length -
            deliveryTotalsForValidation.length
        ) >
          0.01
      ) {
        generalMessage =
          t(
            "errors.pickupDeliveryLengthMismatch"
          );

        valid =
          false;
      }

      if (
        generalMessage
      ) {
        setError(
          generalMessage
        );
      }

      return {
        valid,

        validatedPickups,

        validatedDeliveries,
      };
    };

  const handleSave =
    async (
      event,
      saveOptions = {}
    ) => {
      event?.preventDefault?.();

      if (
        saving
      ) {
        return false;
      }

      const validation =
        validateForm(
          saveOptions
        );

      if (
        !validation.valid
      ) {
        return false;
      }

      const isInternalCarrier =
        Boolean(
          transportCarrier
            ?.isInternal ||
          String(
            transportCarrier
              ?.value ||
              ""
          ) ===
            "__internal__"
        );

      const payload = {
        length:
          toNumberOrNull(
            length
          ),

        width:
          toNumberOrNull(
            width
          ),

        height:
          toNumberOrNull(
            height
          ),

        weight:
          toNumberOrNull(
            weight
          ),

        merchandise:
          normalizeText(
            merchandise
          ),

        reference:
          normalizeText(
            reference
          ) ||
          null,

        price:
          toNumberOrNull(
            price
          ),

        priceCurrency:
          priceCurrency ||
          "EUR",

        price4subcontracted:
          showSubcontractedFields
            ? toNumberOrNull(
                price4subcontracted
              )
            : null,

        notes4driver:
          normalizeText(
            notes4driver
          ) ||
          null,

        notes4subcontracted:
          showSubcontractedFields
            ? normalizeText(
                notes4subcontracted
              ) ||
              null
            : null,

        clientType:
          clientType ||
          null,

        industryClientId:
          clientType ===
            "industry"
            ? selectedClient
                ?.value ??
              null
            : null,

        carrierClientId:
          clientType ===
            "carrier"
            ? selectedClient
                ?.value ??
              null
            : null,

        clientContactEmails:
          selectedClientContactEmails
            .slice(
              0,
              3
            )
            .map(
              (
                option
              ) =>
                option.value
            ),

        carrierId:
          isInternalCarrier
            ? null
            : Number(
                transportCarrier
                  ?.value
              ),

        carrierName:
          isInternalCarrier
            ? companyName ||
              transportCarrier
                ?.label ||
              null
            : transportCarrier
                ?.label ||
              null,

        subcontractedEmails:
          showSubcontractedFields
            ? selectedSubcontractedEmails
                .slice(
                  0,
                  3
                )
                .map(
                  (
                    option
                  ) =>
                    option.value
                )
            : [],

        trailerType:
          trailerType
            ? String(
                trailerType
              )
                .trim()
                .toUpperCase()
            : null,

        trailerTypeOther:
          String(
            trailerType ||
              ""
          )
            .trim()
            .toUpperCase() ===
            "OTHER"
            ? normalizeText(
                trailerTypeOther
              ) ||
              null
            : null,

        loadBearer:
          isLoadBearer,

        specialLoad:
          isSpecialLoad,

        superImposable:
          isSuperImposable,

        associatedLoadBearerId:
          isSuperImposable
            ? load
                ?.associatedLoadBearerId ??
              null
            : load
                ?.associatedLoadBearerId ??
              null,

        pickupLocations:
          validation
            .validatedPickups
            .map(
              (
                point,
                index
              ) =>
                sanitizePoint({
                  ...point,

                  type:
                    "PICKUP",

                  sequence:
                    index,
                })
            ),

        deliveryLocations:
          validation
            .validatedDeliveries
            .map(
              (
                point,
                index
              ) =>
                sanitizePoint({
                  ...point,

                  type:
                    "DELIVERY",

                  sequence:
                    index,
                })
            ),

        pickupPoints:
          validation
            .validatedPickups
            .map(
              (
                point,
                index
              ) =>
                sanitizePoint({
                  ...point,

                  type:
                    "PICKUP",

                  sequence:
                    index,
                })
            ),

        deliveryPoints:
          validation
            .validatedDeliveries
            .map(
              (
                point,
                index
              ) =>
                sanitizePoint({
                  ...point,

                  type:
                    "DELIVERY",

                  sequence:
                    index,
                })
            ),
      };

      try {
        setSaving(
          true
        );

        setError(
          ""
        );

        const response =
          await axios.put(
            `/loads/${loadId}`,
            payload
          );

        const responseData =
          response.data &&
          typeof response.data ===
            "object"
            ? response.data
            : {};

        const responseLoad =
          responseData.load &&
          typeof responseData.load ===
            "object"
            ? responseData.load
            : responseData;

        setLoad(
          (
            current
          ) => ({
            ...(current ||
              {}),

            ...payload,

            ...responseData,
          })
        );

        setSavedEditSnapshot(
          latestEditSnapshotRef.current
        );

        setUnsavedChangesModalOpen(
          false
        );

        setPendingNavigation(
          null
        );

        setSuccessPopupOpen(
          true
        );

        return true;
      } catch (
        requestError
      ) {
        console.error(
          "[EditLoad] failed to update load:",
          requestError
        );

        setError(
          t(
            "errors.updateFailed"
          )
        );

        return false;
      } finally {
        setSaving(
          false
        );
      }
    };

  const handleCloseSuccessPopup =
    () => {
      setSuccessPopupOpen(
        false
      );

      navigate(
        "/loads",
        {
          replace:
            true,
        }
      );
    };

  const requestNavigation =
    (
      to,
      options = {}
    ) => {
      if (
        !hasUnsavedChanges
      ) {
        navigate(
          to,
          options
        );

        return;
      }

      setPendingNavigation({
        type:
          "navigate",

        to,

        options,
      });

      setUnsavedChangesModalOpen(
        true
      );
    };

  const handleContinueEditing =
    () => {
      setUnsavedChangesModalOpen(
        false
      );

      setPendingNavigation(
        null
      );
    };

  const performPendingNavigation =
    (
      navigation
    ) => {
      if (
        !navigation
      ) {
        return;
      }

      bypassHistoryGuardRef.current =
        true;

      historyGuardActiveRef.current =
        false;

      if (
        navigation.type ===
        "navigate"
      ) {
        navigate(
          navigation.to,
          navigation.options ||
          {}
        );

        return;
      }

      if (
        navigation.type ===
        "internal"
      ) {
        const targetUrl =
          new URL(
            navigation.href
          );

        navigate(
          `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
        );

        return;
      }

      if (
        navigation.type ===
        "external"
      ) {
        window.location.assign(
          navigation.href
        );
      }
    };

  const handleLeaveWithoutSaving =
    () => {
      const navigation =
        pendingNavigation;

      setUnsavedChangesModalOpen(
        false
      );

      setPendingNavigation(
        null
      );

      performPendingNavigation(
        navigation
      );
    };

  const handleSaveAndLeave =
    async () => {
      const navigation =
        pendingNavigation;

      setUnsavedChangesModalOpen(
        false
      );

      const saved =
        await handleSave(
          null
        );

      if (
        !saved
      ) {
        return;
      }

      setSuccessPopupOpen(
        false
      );

      setPendingNavigation(
        null
      );

      performPendingNavigation(
        navigation
      );
    };

  const handleBack =
    () => {
      requestNavigation(
        "/loads",
        {
          replace:
            true,
        }
      );
    };

  return (
    <>
    <CurrentLoadWorkspace
      ready={
        ready
      }
      fetching={
        fetching
      }
      saving={
        saving
      }
      error={
        error
      }
      successPopupOpen={
        successPopupOpen
      }
      onCloseSuccessPopup={
        handleCloseSuccessPopup
      }
      onBack={
        handleBack
      }
      onSave={
        handleSave
      }
      load={
        load
      }
      loadId={
        loadId
      }
      loadNumber={
        loadNumber
      }
      transportSessionNumber={
        transportSessionNumber
      }
      primaryMode={
        primaryMode
      }
      isLoadBearer={
        isLoadBearer
      }
      isSpecialLoad={
        isSpecialLoad
      }
      isSuperImposable={
        isSuperImposable
      }
      companyId={
        companyId
      }
      companyName={
        companyName
      }
      pickupPoints={
        pickupPoints
      }
      setPickupPoints={
        setPickupPoints
      }
      deliveryPoints={
        deliveryPoints
      }
      setDeliveryPoints={
        setDeliveryPoints
      }
      maxPoints={
        MAX_POINTS
      }
      onAddPickup={
        handleAddPickup
      }
      onAddDelivery={
        handleAddDelivery
      }
      onRemovePickup={
        handleRemovePickup
      }
      onRemoveDelivery={
        handleRemoveDelivery
      }
      placeOptions={
        placeOptions
      }
      loadingAccessOptions={
        loadingAccessOptions
      }
      handlingEquipmentOptions={
        handlingEquipmentOptions
      }
      length={
        length
      }
      width={
        width
      }
      height={
        height
      }
      weight={
        weight
      }
      merchandise={
        merchandise
      }
      reference={
        reference
      }
      price={
        price
      }
      priceCurrency={
        priceCurrency
      }
      price4subcontracted={
        price4subcontracted
      }
      notes4driver={
        notes4driver
      }
      notes4subcontracted={
        notes4subcontracted
      }
      trailerType={
        trailerType
      }
      trailerTypeOther={
        trailerTypeOther
      }
      setLength={
        setLength
      }
      setWidth={
        setWidth
      }
      setHeight={
        setHeight
      }
      setWeight={
        setWeight
      }
      setMerchandise={
        setMerchandise
      }
      setReference={
        setReference
      }
      setPrice={
        setPrice
      }
      setPriceCurrency={
        setPriceCurrency
      }
      setPrice4subcontracted={
        setPrice4subcontracted
      }
      setNotes4driver={
        setNotes4driver
      }
      setNotes4subcontracted={
        setNotes4subcontracted
      }
      setTrailerType={
        setTrailerType
      }
      setTrailerTypeOther={
        setTrailerTypeOther
      }
      clientType={
        clientType
      }
      setClientType={(
        nextClientType
      ) => {
        setClientType(
          nextClientType ||
          ""
        );

        setSelectedClient(
          null
        );

        setSelectedClientContactEmails(
          []
        );

        setClientTypeError(
          ""
        );

        setClientError(
          ""
        );
      }}
      clientTypeOptions={
        clientTypeOptions
      }
      selectedClient={
        selectedClient
      }
      setSelectedClient={(
        nextClient
      ) => {
        setSelectedClient(
          nextClient
        );

        setSelectedClientContactEmails(
          []
        );

        setClientError(
          ""
        );
      }}
      clientOptions={
        clientOptions
      }
      selectedClientContactEmails={
        selectedClientContactEmails
      }
      setSelectedClientContactEmails={
        setSelectedClientContactEmails
      }
      selectedClientContactEmailOptions={
        selectedClientContactEmailOptions
      }
      transportCarrier={
        transportCarrier
      }
      setTransportCarrier={(
        nextCarrier
      ) => {
        setTransportCarrier(
          nextCarrier ||
          internalCarrierOption
        );

        setTransportCarrierError(
          ""
        );

        setSelectedSubcontractedEmails(
          []
        );
      }}
      transportCarrierOptions={
        transportCarrierOptions
      }
      showSubcontractedFields={
        showSubcontractedFields
      }
      selectedSubcontractedEmails={
        selectedSubcontractedEmails
      }
      setSelectedSubcontractedEmails={
        setSelectedSubcontractedEmails
      }
      selectedSubcontractedEmailOptions={
        selectedSubcontractedEmailOptions
      }
      trailerTypeOptions={
        trailerTypeOptions
      }
      merchandiseError={
        merchandiseError
      }
      weightError={
        weightError
      }
      lengthError={
        lengthError
      }
      widthError={
        widthError
      }
      heightError={
        heightError
      }
      priceError={
        priceError
      }
      price4subcontractedError={
        price4subcontractedError
      }
      trailerTypeError={
        trailerTypeError
      }
      trailerTypeOtherError={
        trailerTypeOtherError
      }
      clientTypeError={
        clientTypeError
      }
      clientError={
        clientError
      }
      transportCarrierError={
        transportCarrierError
      }
      setMerchandiseError={
        setMerchandiseError
      }
      setWeightError={
        setWeightError
      }
      setPriceError={
        setPriceError
      }
      setPrice4subcontractedError={
        setPrice4subcontractedError
      }
      setTrailerTypeError={
        setTrailerTypeError
      }
      setTrailerTypeOtherError={
        setTrailerTypeOtherError
      }
      setClientTypeError={
        setClientTypeError
      }
      setClientError={
        setClientError
      }
      setTransportCarrierError={
        setTransportCarrierError
      }
      dimensionLimits={
        dimensionLimits
      }
      bearerMinimumLength={
        bearerMinimumLength
      }
      loadBearerInfo={
        loadBearerInfo
      }
      associatedLoadBearer={
        associatedLoadBearer
      }
      setAssociatedLoadBearer={
        setAssociatedLoadBearer
      }
      superimposableAvailableLength={
        superimposableAvailableLength
      }
      superimposableAvailableHeight={
        superimposableAvailableHeight
      }
    />

      {unsavedChangesModalOpen && (
        <div
          className="current-load-unsaved-overlay"
          role="presentation"
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget
            ) {
              handleContinueEditing();
            }
          }}
        >
          <div
            className="current-load-unsaved-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="current-load-unsaved-title"
          >
            <div className="current-load-unsaved-modal__icon">
              !
            </div>

            <div className="current-load-unsaved-modal__content">
              <h2 id="current-load-unsaved-title">
                {t(
                  "workspace.unsavedChanges.title"
                )}
              </h2>

              <p>
                {t(
                  "workspace.unsavedChanges.message"
                )}
              </p>
            </div>

            <div className="current-load-unsaved-modal__actions">
              <button
                type="button"
                className="current-load-unsaved-modal__continue"
                onClick={
                  handleContinueEditing
                }
              >
                {t(
                  "workspace.unsavedChanges.continueEditing"
                )}
              </button>

              <button
                type="button"
                className="current-load-unsaved-modal__save"
                onClick={
                  handleSaveAndLeave
                }
                disabled={
                  saving
                }
              >
                {saving
                  ? t(
                      "common.saving"
                    )
                  : t(
                      "workspace.unsavedChanges.saveAndLeave"
                    )}
              </button>

              <button
                type="button"
                className="current-load-unsaved-modal__leave"
                onClick={
                  handleLeaveWithoutSaving
                }
              >
                {t(
                  "workspace.unsavedChanges.leaveWithoutSaving"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}